import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { ExportButton } from '@/components/common/ExportButton';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { IntervalList, type IntervalRow } from '@/pages/anatel/conselho/IntervalList';
import { EvidenceCard, metricRows, OrderNote, ReadingNote } from '@/pages/anatel/conselho/shared';
import { RunFooter } from '@/pages/anatel/conselho/RunFooter';
import { RubricTable } from '@/pages/anatel/conselho/RubricTable';
import { ValidationCard } from '@/pages/anatel/conselho/ValidationCard';
import { fetchAnatelConselho } from '@/services/anatel';
import type {
  ConselhoMetric,
  ConselhoPair,
  ConselhoPosition,
  ConselhoRosterMember,
} from '@/services/types';

/**
 * The Conselho Diretor bench: model-derived decision patterns per member.
 *
 * Nothing on this page is computed in the browser or in the API. Each panel is
 * READ from the latest accepted run of a model the ETL fitted on the roll call
 * it rebuilt from the minutes, and each panel names that run. The language is
 * the blueprint's: adjusted association, estimated position, propensity --
 * and every figure carries its n and its 90% credible interval, with members
 * below the sample floor listed in grey and never ranked.
 */
export function AnatelConselhoPage() {
  const { t, i18n } = useTranslation(['anatel', 'common']);
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['anatel', 'conselho'],
    queryFn: fetchAnatelConselho,
  });

  if (isPending) {
    return (
      <div className="space-y-4 p-6">
        <SkeletonLoader rows={3} />
        <SkeletonLoader rows={8} />
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-6">
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      </div>
    );
  }

  const signed = new Intl.NumberFormat(i18n.language, { signDisplay: 'exceptZero', minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const percent = new Intl.NumberFormat(i18n.language, { style: 'percent', maximumFractionDigits: 1 });
  const plain = new Intl.NumberFormat(i18n.language, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const pp = (v: number) => `${signed.format(v)} pp`;
  const logit = (v: number) => signed.format(v);
  const personLink = (row: IntervalRow) => (row.slug ? `/anatel/r/${row.slug}` : null);

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('anatel:conselho.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('anatel:conselho.subtitle')}</p>
        <Link
          to="/anatel/conselho/methodology"
          className="inline-block text-xs text-primary underline-offset-4 hover:underline"
          data-testid="methodology-link"
        >
          {t('common:conselhoMethodology.link')}
        </Link>
      </header>

      <ExplainerCard i18nKey="anatel:conselho.explainer" />
      <FieldGuide i18nKey="anatel:conselho.guide" level={2} />

      <div className="flex justify-end">
        <ExportButton path="/api/v1/anatel/export/conselho" />
      </div>

      <AsOfStrip asOf={data.as_of} />

      <EvidenceCard evidence={data.evidence} />

      <section className="rounded-lg border bg-card p-4" data-panel="roster">
        <h2 className="mb-3 text-sm font-semibold text-foreground">{t('anatel:conselho.roster.title')}</h2>
        <Answer answer={data.roster} help={t('anatel:conselho.roster.help')}>
          {(members) => <RosterTable members={members} />}
        </Answer>
      </section>

      <section className="rounded-lg border bg-card p-4" data-panel="severity">
        <h2 className="mb-3 text-sm font-semibold text-foreground">{t('anatel:conselho.severity.title')}</h2>
        <ReadingNote reading={data.person_reading} />
        <OrderNote order={data.severity_order} />
        <Answer answer={data.severity} help={t('anatel:conselho.severity.help')}>
          {(rows) => (
            <IntervalList
              rows={metricRows(rows)}
              axisLabel={t('anatel:conselho.severity.axis')}
              format={logit}
              rawFormat={(v) => percent.format(v)}
              linkTo={personLink}
            />
          )}
        </Answer>
        <h3 className="mb-2 mt-4 text-xs font-semibold text-foreground">{t('anatel:conselho.severity.pp')}</h3>
        <Answer answer={data.severity_pp} help={t('anatel:conselho.severity.ppHelp')}>
          {(rows) => (
            <IntervalList rows={metricRows(rows)} axisLabel={t('anatel:conselho.severity.ppAxis')} format={pp} linkTo={personLink} />
          )}
        </Answer>
        {data.severity_variance.length > 0 ? (
          <div className="mt-4">
            <h3 className="mb-1 text-xs font-semibold text-foreground">{t('anatel:conselho.severity.variance')}</h3>
            <p className="mb-2 text-[11px] text-muted-foreground">{t('anatel:conselho.severity.varianceHelp')}</p>
            <dl className="grid gap-x-4 gap-y-1 text-xs sm:grid-cols-3" data-testid="variance-components">
              {data.severity_variance.map((v) => (
                <div key={v.metric}>
                  <dt className="text-muted-foreground">{v.metric}</dt>
                  <dd className="tabular-nums text-foreground">
                    {plain.format(v.value)}{' '}
                    <span className="text-muted-foreground">
                      [{plain.format(v.lower)}, {plain.format(v.upper)}]
                    </span>
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        ) : null}
        <ValidationCard validation={data.validation} />
        <RunFooter run={data.severity_run} />
      </section>

      <section className="rounded-lg border bg-card p-4" data-panel="dissent">
        <h2 className="mb-3 text-sm font-semibold text-foreground">{t('anatel:conselho.dissent.title')}</h2>
        <Answer answer={data.dissent} help={t('anatel:conselho.dissent.help')}>
          {(rows) => <DissentTable rows={rows} />}
        </Answer>
        <h3 className="mb-2 mt-4 text-xs font-semibold text-foreground">{t('anatel:conselho.dissent.followTitle')}</h3>
        <OrderNote order={data.follow_order} />
        <Answer answer={data.follow} help={t('anatel:conselho.dissent.followHelp')}>
          {(rows) => (
            <IntervalList
              rows={metricRows(rows)}
              axisLabel={t('anatel:conselho.dissent.followAxis')}
              format={logit}
              rawFormat={(v) => percent.format(v)}
              linkTo={personLink}
            />
          )}
        </Answer>
        {data.followed ? (
          <div data-panel="followed">
            <h3 className="mb-2 mt-4 text-xs font-semibold text-foreground">{t('anatel:conselho.followed.title')}</h3>
            <OrderNote order={data.followed_order} />
            <Answer answer={data.followed} help={t('anatel:conselho.followed.help')}>
              {(rows) => (
                <IntervalList
                  rows={metricRows(rows)}
                  axisLabel={t('anatel:conselho.followed.axis')}
                  format={logit}
                  rawFormat={(v) => percent.format(v)}
                  rawLabel={t('anatel:conselho.followed.raw')}
                  linkTo={personLink}
                />
              )}
            </Answer>
          </div>
        ) : null}
        <RunFooter run={data.follow_run} />
      </section>

      <section className="rounded-lg border bg-card p-4" data-panel="pairs">
        <h2 className="mb-3 text-sm font-semibold text-foreground">{t('anatel:conselho.pairs.title')}</h2>
        <Answer answer={data.pairs} help={t('anatel:conselho.pairs.help')}>
          {(pairs) => <PairsTable pairs={pairs} />}
        </Answer>
      </section>

      <section className="rounded-lg border bg-card p-4" data-panel="positions">
        <h2 className="mb-3 text-sm font-semibold text-foreground">{t('anatel:conselho.positions.title')}</h2>
        <Answer answer={data.positions} help={t('anatel:conselho.positions.help', { floor: 15 })}>
          {(rows) => (
            <IntervalList
              rows={positionRows(rows, t('anatel:conselho.positions.contested'))}
              axisLabel={t('anatel:conselho.positions.axis')}
              format={logit}
              linkTo={personLink}
            />
          )}
        </Answer>
        <RunFooter run={data.position_run} />
        <h3 className="mb-2 mt-4 text-xs font-semibold text-foreground">{t('anatel:conselho.positions.propensityTitle')}</h3>
        <Answer answer={data.propensities} help={t('anatel:conselho.positions.propensityHelp')}>
          {(rows) => (
            <IntervalList
              rows={propensityRows(rows, t('anatel:conselho.positions.items'))}
              axisLabel={t('anatel:conselho.positions.propensityAxis')}
              format={logit}
              zeroLine={false}
              linkTo={personLink}
            />
          )}
        </Answer>
        <RunFooter run={data.propensity_run} />
        <Link
          to="/anatel/conselho/items"
          className="mt-3 inline-block text-xs text-primary underline-offset-4 hover:underline"
        >
          {t('anatel:conselho.items.seeAll')}
        </Link>
      </section>

      <section className="rounded-lg border bg-card p-4" data-panel="style">
        <h2 className="mb-1 text-sm font-semibold text-foreground">{t('anatel:conselho.style.title')}</h2>
        <p className="mb-3 text-[11px] text-muted-foreground">{t('anatel:conselho.style.intro')}</p>
        <h3 className="mb-2 text-xs font-semibold text-foreground">{t('anatel:conselho.rubric.title')}</h3>
        <Answer answer={data.rubric} help={t('anatel:conselho.rubric.help')}>
          {(rows) => <RubricTable rows={rows} />}
        </Answer>
        <RunFooter run={data.rubric_run} />
        <h3 className="mb-2 mt-4 text-xs font-semibold text-foreground">{t('anatel:conselho.style.residualTitle')}</h3>
        <Answer answer={data.style_residual} help={t('anatel:conselho.style.residualHelp')}>
          {(rows) => (
            <IntervalList
              rows={metricRows(rows)}
              axisLabel={t('anatel:conselho.style.residualAxis')}
              format={logit}
              rawFormat={(v) => percent.format(v)}
              rawLabel={t('anatel:conselho.style.favorableShare')}
              linkTo={personLink}
            />
          )}
        </Answer>
        <RunFooter run={data.projection_run} />
        <h3 className="mb-2 mt-4 text-xs font-semibold text-foreground">{t('anatel:conselho.style.wordfishTitle')}</h3>
        <Answer answer={data.wordfish} help={t('anatel:conselho.style.wordfishHelp')}>
          {(rows) => (
            <IntervalList rows={metricRows(rows)} axisLabel={t('anatel:conselho.style.wordfishAxis')} format={logit} linkTo={personLink} />
          )}
        </Answer>
        <RunFooter run={data.wordfish_run} />
      </section>
    </div>
  );
}

function positionRows(rows: ConselhoPosition[], nLabel: string): IntervalRow[] {
  return rows.map((r) => ({
    key: `${r.name}|${r.period}`,
    name: r.period === 'all' ? r.name : `${r.name} — ${r.period}`,
    slug: r.slug,
    n: r.n_contested,
    nLabel,
    effect: r.position,
    lower: r.lower,
    upper: r.upper,
    suppressed: r.suppressed,
  }));
}

function propensityRows(rows: ConselhoPosition[], nLabel: string): IntervalRow[] {
  return rows.map((r) => ({
    key: `${r.name}|${r.period}`,
    name: r.name,
    slug: r.slug,
    n: r.n_items,
    nLabel,
    effect: r.dissent_propensity,
    lower: r.lower,
    upper: r.upper,
    suppressed: r.suppressed,
  }));
}

function RosterTable({ members }: { members: ConselhoRosterMember[] }) {
  const { t, i18n } = useTranslation(['anatel']);
  const number = new Intl.NumberFormat(i18n.language);
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="sticky top-0 z-10 bg-muted text-left">
          <tr>
            <th className="p-2">{t('anatel:conselho.roster.name')}</th>
            <th className="p-2">{t('anatel:conselho.roster.roles')}</th>
            <th className="p-2">{t('anatel:conselho.roster.span')}</th>
            <th className="p-2 text-right">{t('anatel:conselho.roster.relatorias')}</th>
            <th className="p-2 text-right">{t('anatel:conselho.roster.presences')}</th>
            <th className="p-2 text-right">{t('anatel:conselho.roster.analises')}</th>
            <th className="p-2 text-right">{t('anatel:conselho.roster.votos')}</th>
          </tr>
        </thead>
        <tbody>
          {members.map((m) => (
            <tr key={m.slug} className="border-t">
              <td className="p-2">
                <Link to={`/anatel/r/${m.slug}`} className="text-primary underline-offset-4 hover:underline">
                  {m.name}
                </Link>
              </td>
              <td className="p-2 text-muted-foreground">{m.roles.join(', ')}</td>
              <td className="p-2 tabular-nums text-muted-foreground">
                {m.first_seen && m.last_seen ? `${m.first_seen} — ${m.last_seen}` : '—'}
              </td>
              <td className="p-2 text-right tabular-nums">{number.format(m.n_relatorias)}</td>
              <td className="p-2 text-right tabular-nums">{number.format(m.n_presences)}</td>
              <td className="p-2 text-right tabular-nums">{number.format(m.n_analises)}</td>
              <td className="p-2 text-right tabular-nums">{number.format(m.n_votos)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DissentTable({ rows }: { rows: ConselhoMetric[] }) {
  const { t, i18n } = useTranslation(['anatel']);
  const number = new Intl.NumberFormat(i18n.language);
  const percent = new Intl.NumberFormat(i18n.language, { style: 'percent', maximumFractionDigits: 1 });
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs" data-testid="dissent-table">
        <thead className="sticky top-0 z-10 bg-muted text-left">
          <tr>
            <th className="p-2">{t('anatel:conselho.roster.name')}</th>
            <th className="p-2 text-right">{t('anatel:conselho.dissent.opportunities')}</th>
            <th className="p-2 text-right">{t('anatel:conselho.dissent.rate')}</th>
            <th className="p-2 text-right">{t('anatel:conselho.dissent.interval')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.name} className={`border-t ${r.suppressed ? 'text-muted-foreground' : ''}`} data-suppressed={r.suppressed ? 'true' : 'false'}>
              <td className="p-2">
                <Link to={`/anatel/r/${r.slug}`} className="underline-offset-4 hover:underline">
                  {r.name}
                </Link>
              </td>
              <td className="p-2 text-right tabular-nums">{number.format(r.n)}</td>
              <td className="p-2 text-right tabular-nums">
                {r.raw_rate !== null ? percent.format(r.raw_rate) : '—'}
                {r.suppressed ? ` (${t('anatel:conselho.pairs.belowFloor')})` : ''}
              </td>
              <td className="p-2 text-right tabular-nums text-muted-foreground">
                {r.lower !== null && r.upper !== null ? `[${percent.format(r.lower)}, ${percent.format(r.upper)}]` : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="mt-1 text-[11px] text-muted-foreground">{t('anatel:conselho.dissent.intervalNote')}</p>
    </div>
  );
}

function PairsTable({ pairs }: { pairs: ConselhoPair[] }) {
  const { t, i18n } = useTranslation(['anatel']);
  const number = new Intl.NumberFormat(i18n.language);
  const percent = new Intl.NumberFormat(i18n.language, { style: 'percent', maximumFractionDigits: 0 });
  const listed = pairs.filter((p) => !p.suppressed);
  const flagged = pairs.length - listed.length;
  return (
    <div className="space-y-2">
      <div className="max-h-96 overflow-auto">
        <table className="w-full text-xs" data-testid="pairs-table">
          <thead className="sticky top-0 z-10 bg-muted text-left">
            <tr>
              <th className="p-2">A</th>
              <th className="p-2">B</th>
              <th className="p-2 text-right">{t('anatel:conselho.pairs.coSat')}</th>
              <th className="p-2 text-right">{t('anatel:conselho.pairs.agree')}</th>
              <th className="p-2 text-right">{t('anatel:conselho.pairs.contested')}</th>
              <th className="p-2 text-right">{t('anatel:conselho.pairs.agreeContested')}</th>
            </tr>
          </thead>
          <tbody>
            {listed.map((p) => (
              <tr key={`${p.slug_a}|${p.slug_b}`} className="border-t">
                <td className="p-2">{p.name_a}</td>
                <td className="p-2">{p.name_b}</td>
                <td className="p-2 text-right tabular-nums">{number.format(p.n_co_sat)}</td>
                <td className="p-2 text-right tabular-nums">
                  {p.agreement_rate !== null ? percent.format(p.agreement_rate) : '—'}
                </td>
                <td className="p-2 text-right tabular-nums">{number.format(p.n_contested_co_sat)}</td>
                <td className="p-2 text-right tabular-nums">
                  {p.agreement_rate_contested !== null ? percent.format(p.agreement_rate_contested) : '—'}
                  {p.lower_contested != null && p.upper_contested != null ? (
                    <span className="ml-1 text-muted-foreground" data-testid="pair-interval">
                      [{percent.format(p.lower_contested)}, {percent.format(p.upper_contested)}]
                    </span>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-muted-foreground">{t('anatel:conselho.dissent.intervalNote')}</p>
      {flagged > 0 ? (
        <p className="text-[11px] text-muted-foreground" data-testid="pairs-flagged">
          {number.format(flagged)} {t('anatel:conselho.pairs.belowFloor')}
        </p>
      ) : null}
    </div>
  );
}
